import { useState, useCallback } from 'react'
import { toast } from 'react-toastify'
import { postCPDataWithParams } from 'src/clinic/services'
import { getMPDataWithoutParams } from 'src/mentor/services'
import { addOrUpdateInvestigationInIdb } from 'src/emr/database/indexedDbDao'
import { INVESTIGATION_OBJECT } from 'src/clinic/components/InvestigationsForm/InvestigationsForm'
import { defaultConfirmationModalProps, genericHandleChange } from 'src/consumer/helpers/utilFunctions'

const INVESTIGATION_SAMPLES_CACHE_TTL_MS = 5 * 60 * 1000
const investigationSamplesCache = {
  data: null,
  timestamp: 0,
}
let inFlightInvestigationSamplesPromise = null

/**
 * Custom hook for managing investigation data and form functionality
 * @param {string} clinicId - Clinic ID
 * @param {string} clinicBrandId - Clinic brand ID
 * @returns {Object} Investigation data and functions
 */
export const useInvestigationData = (clinicId, clinicBrandId) => {
  // Investigation form state
  const [investigationToEdit, setInvestigationToEdit] = useState(null)
  const [initialInvestigationObjectToEdit, setInitialInvestigationObjectToEdit] = useState(null)
  const [showInvestigationForm, setShowInvestigationForm] = useState(false)
  const [savingInvestigation, setSavingInvestigation] = useState(false)
  const [investigationTitleValid, setInvestigationTitleValid] = useState(true)

  // Sample types and confirmation modal
  const [sampleTypes, setSampleTypes] = useState([])
  const [confirmationModalProps, setConfirmationModalProps] = useState(defaultConfirmationModalProps)

  /**
   * Open investigation form with optional initial value
   * @param {string} inputValue - Initial title value for the investigation
   */
  const openInvestigationForm = useCallback((inputValue = '') => {
    const investigationObj = {
      ...INVESTIGATION_OBJECT,
      title: inputValue,
    }

    setInvestigationToEdit(investigationObj)
    setInitialInvestigationObjectToEdit(investigationObj)
    setShowInvestigationForm(true)
  }, [])

  /**
   * Close investigation form
   */
  const closeInvestigationForm = useCallback(() => {
    setShowInvestigationForm(false)
    setInvestigationToEdit(null)
    setInitialInvestigationObjectToEdit(null)
  }, [])

  /**
   * Handle changes to investigation form fields
   * @param {Event} e - Form input event
   */
  const handleChangeInvestigationToEdit = useCallback((e) => {
    genericHandleChange(e, setInvestigationToEdit)
  }, [])

  /**
   * Fetch investigation sample types
   * @returns {Promise<Array>} Array of sample types
   */
  const getInvestigationSamples = useCallback(async () => {
    try {
      const hasFreshCache =
        Array.isArray(investigationSamplesCache.data) &&
        Date.now() - investigationSamplesCache.timestamp < INVESTIGATION_SAMPLES_CACHE_TTL_MS
      if (hasFreshCache) {
        setSampleTypes(investigationSamplesCache.data)
        return investigationSamplesCache.data
      }

      if (inFlightInvestigationSamplesPromise) {
        const shared = await inFlightInvestigationSamplesPromise
        setSampleTypes(Array.isArray(shared) ? shared : [])
        return Array.isArray(shared) ? shared : []
      }

      inFlightInvestigationSamplesPromise = getMPDataWithoutParams('/api/secure/practitioner/clinical/investigation-sample/list')
      const response = await inFlightInvestigationSamplesPromise
      if (response?.status === 200) {
        const resolvedData = Array.isArray(response?.data) ? response.data : []
        investigationSamplesCache.data = resolvedData
        investigationSamplesCache.timestamp = Date.now()
        setSampleTypes(resolvedData)
        return resolvedData
      }
      return []
    } catch (error) {
      console.error('Error fetching investigation samples:', error)
      return []
    } finally {
      inFlightInvestigationSamplesPromise = null
    }
  }, [])

  /**
   * Save investigation to the backend
   * @returns {Promise<boolean>} Success status
   */
  const saveInvestigation = useCallback(async () => {
    if (!investigationToEdit || !clinicId || !clinicBrandId) {
      return false
    }

    const tst = toast.loading('Saving Investigation')
    setSavingInvestigation(true)

    try {
      const response = await postCPDataWithParams('/api/secure/doctor/investigation/save', {
        ...investigationToEdit,
        clinicId,
        clinicBrandIds: [clinicBrandId],
        clinicIds: [clinicId],
        source: 'doctor',
      })

      if (response?.status === 200) {
        addOrUpdateInvestigationInIdb([response?.data?.data?.investigation])
        toast.update(tst, {
          render: 'Investigation Saved',
          type: toast.TYPE.SUCCESS,
          isLoading: false,
          autoClose: 2000,
        })
        setShowInvestigationForm(false)
        setSavingInvestigation(false)
        return true
      }

      toast.update(tst, {
        render: 'Investigation could not be saved',
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 2000,
      })
      setSavingInvestigation(false)
      return false
    } catch (error) {
      console.error('Error saving investigation:', error)
      toast.update(tst, {
        render: 'Investigation could not be saved',
        type: toast.TYPE.ERROR,
        isLoading: false,
        autoClose: 2000,
      })
      setSavingInvestigation(false)
      return false
    }
  }, [investigationToEdit, clinicId, clinicBrandId])

  /**
   * Submit investigation with validation and confirmation
   * @param {Event} event - Form submit event
   */
  const submitInvestigation = useCallback((event) => {
    const form = event.currentTarget
    if (!form.checkValidity()) {
      event.preventDefault()
      event.stopPropagation()
    } else {
      event.preventDefault()
      setConfirmationModalProps((prev) => ({
        ...prev,
        action: 'save',
        body: 'Are you sure you want to save this investigation?',
        visibility: true,
        visibilitySetter: setConfirmationModalProps,
        onSubmitFunctions: [
          () => {
            saveInvestigation()
          },
        ],
      }))
    }
  }, [saveInvestigation])

  /**
   * Reset investigation form to initial state
   */
  const resetInvestigationForm = useCallback(() => {
    setInvestigationToEdit(initialInvestigationObjectToEdit)
  }, [initialInvestigationObjectToEdit])

  /**
   * Validate investigation title
   * @param {string} title - Investigation title to validate
   * @returns {boolean} Validation result
   */
  const validateInvestigationTitle = useCallback((title) => {
    const isValid = !!(title && title.trim().length > 0)
    setInvestigationTitleValid(isValid)
    return isValid
  }, [])

  return {
    // Form state
    investigationToEdit,
    initialInvestigationObjectToEdit,
    showInvestigationForm,
    savingInvestigation,
    investigationTitleValid,

    // Sample types
    sampleTypes,

    // Confirmation modal
    confirmationModalProps,
    setConfirmationModalProps,

    // Setters
    setInvestigationToEdit,
    setShowInvestigationForm,
    setSavingInvestigation,
    setInvestigationTitleValid,

    // Functions
    openInvestigationForm,
    closeInvestigationForm,
    handleChangeInvestigationToEdit,
    getInvestigationSamples,
    saveInvestigation,
    submitInvestigation,
    resetInvestigationForm,
    validateInvestigationTitle,
  }
}

export default useInvestigationData